import { useState } from "react";
import { Button } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import { useMoney } from "@/shared/ui";
import type { InvoicePayment } from "@/lib/api/types";
import { RequestCard } from "@/modules/pos/components/RequestCard";

/**
 * A DINER'S CLAIM to have paid this bill (D44), on the till.
 *
 * The wording is the whole point: "the customer says they paid", never "paid".
 * A screenshot of a bank app is a claim, and confirming it is a person deciding
 * they believe it — so the card offers a look at the image and two verdicts,
 * and says out loud that confirming does not settle anything. Settling stays
 * the separate, deliberate act it always was.
 *
 * CASH and CARD claims carry no image, because there is nothing to show: they
 * are a diner telling the till "we're ready, we'll pay at the counter". The
 * card says that instead of offering a View button that opens nothing.
 *
 * Only a SUBMITTED claim gets buttons. A decided one stays visible as a line of
 * history — a cashier who confirmed a transfer needs to see that they did, and
 * so does the next person on the shift.
 *
 * **AND IT SAYS WHEN THE CLAIM IS NOT THE BILL (B118 round table).**
 * `payment.amount` is the server's figure AT SUBMISSION, and the bill moves
 * after it: a round fired while the diner was in their banking app, a plate
 * struck, a service charge landing when the kitchen accepted. The card printed
 * that frozen figure in bold beside the word "paid" and nothing else, so a
 * cashier comparing it against the bill on the same screen had to notice two
 * numbers in two type sizes disagreeing by eighty pounds — at the counter, at
 * 1am, with the diner standing there.
 *
 * Both directions are named, because they are different problems. SHORT is the
 * ordinary one and the till has a remedy: confirm and take the rest. OVER is the
 * one with no route back through this product — a transfer that arrived too
 * large cannot be handed change out of a drawer it never reached (D44), and the
 * only honest thing the screen can do is say so before the cashier taps confirm
 * rather than after.
 *
 * **A SHARE AND A TIP (D133).** A claim may be one diner's share — the bill
 * split evenly, or their own dishes — and may carry a tip. The card says which
 * share it is, the tip apart ("for the team, not on the bill"), and what to
 * collect in all, and offers "Settle this share": the page opens the Settle
 * window on exactly that share (the typed amount for an even share, the dishes
 * picked for an items share), so the cashier settles what the diner claimed
 * through the ordinary part payment (D126) and the bill stays open until the
 * last share. A share is SMALLER than the bill by design, so it is never
 * badged short; only a share above what is still owed is flagged. A cash tip
 * can be put in the drawer in one tap (a `TIP_IN` keyed on the claim, so a
 * second tap records nothing more).
 *
 * **WHO IS ASKING WHAT (D133 fix).** Only an INSTAPAY claim is "says they
 * paid" — the transfer happened and the screenshot is the claim. A CASH or
 * CARD claim is the diner ASKING to pay at the table; nothing has changed
 * hands, so its headline says that.
 *
 * **SETTLED FROM THE CARD (D133 fix).** "Settle this share" makes the page send
 * the claim's id with the settle; the server stamps the claim (`shareSettled`)
 * and, when the tender was the claim unchanged, confirms it in the same act.
 * A stamped claim is never offered for settling again — the button gives way
 * to "This share is settled" — and, if the cashier changed the share in the
 * window, it still waits for Confirm or Refuse.
 */
export function DinerPaymentCard({
  payment,
  invoiceTotal,
  isSubmitting,
  onReview,
  onSettleShare,
  onRecordCashTip,
}: {
  payment: InvoicePayment;
  /**
   * What the bill still owes NOW — its running total less what has already
   * been paid toward it (D126/D133). Optional because one caller site is not
   * the whole product: a screen that cannot say what the bill is should show
   * the claim without a comparison rather than invent one against zero.
   */
  invoiceTotal?: number | null;
  isSubmitting: boolean;
  onReview: (verified: boolean) => void;
  /** D133 — open the Settle window on exactly this claim's share. */
  onSettleShare?: () => void;
  /**
   * D133 — record a CASH claim's tip as a `TIP_IN` in the cashier's drawer.
   * Resolves `true` once recorded. Absent for anyone without an open drawer.
   */
  onRecordCashTip?: () => Promise<boolean>;
}) {
  const t = useTranslate();
  const money = useMoney();
  const [tipRecorded, setTipRecorded] = useState(false);
  const [tipPending, setTipPending] = useState(false);
  const open = payment.state === "SUBMITTED";
  const shareSettled = payment.shareSettled === true;
  const split = payment.split ?? null;
  const tipAmount = payment.tipAmount ?? 0;
  /*
   * Only on a LIVE claim. A decided one is history, and second-guessing a
   * confirmation the cashier already made — against a total that has moved since
   * — would be an accusation rather than information.
   *
   * A SHARE (D133) is smaller than the bill on purpose, so only the OVER
   * direction means anything for one: a share above what is still owed.
   */
  /*
   * Only for a TRANSFER, and only before any of it was settled (2026-10-27).
   * A cash or card claim is an ASK: nothing has changed hands, so there is no
   * sent amount to hold against the bill — and the OVER sentence is about a
   * transfer that reached no drawer. Once a settle came from the claim, the
   * money it took is already off what is owed, so comparing the claim with
   * the remainder counted it twice: a 335 ask settled as 240 read "more by
   * 240" against the 95 left.
   */
  const isAsk = payment.method !== "INSTAPAY";
  const rawGap =
    open && !isAsk && !shareSettled && typeof invoiceTotal === "number"
      ? Math.round((invoiceTotal - payment.amount) * 100) / 100
      : 0;
  const gap = split !== null && rawGap > 0 ? 0 : rawGap;
  /*
   * A cash or card ask the cashier settled with a DIFFERENT tender: the server
   * leaves it open for a verdict (D133 fix), but for an ask the settle WAS the
   * answer. Confirming would tell the diner's phone "do not pay again" while
   * the rest is still owed, so the card shows what is left instead and the
   * rest is collected with the panel's own Settle.
   */
  const askPartlySettled = open && isAsk && shareSettled;

  const methodLabel = t((m) =>
    payment.method === "INSTAPAY"
      ? m.pos.dinerPaymentInstapay
      : payment.method === "CARD"
        ? m.pos.dinerPaymentCard
        : m.pos.dinerPaymentCash,
  );

  /*
   * ONE ANSWER PER STATE (Trioty canvas, "Table panel · guest payment"). The
   * card is the green request card, and it offers the single act the cashier
   * gives next; the rest is a quiet link.
   *
   * - A cash or card ASK with a settle open to it: take the money. "Collect"
   *   (or "Settle this share" for a split) opens the Settle window on the
   *   claim, and the server confirms the claim in that same act (D133 fix) —
   *   so the cashier is never asked to "confirm" money not yet in hand.
   * - Anything else still open (a transfer; a share settled with a changed
   *   tender; no settle on this till): the verdict, Confirm.
   * - Confirmed, its share not yet settled: settle it.
   * Refusing is always the link beside the button, never a second button.
   */
  const settleable = !shareSettled && Boolean(onSettleShare);
  const collectFirst = open && settleable && payment.method !== "INSTAPAY";
  const settleAfter = payment.state === "VERIFIED" && settleable;
  const collectAmount = money(Math.round((payment.amount + tipAmount) * 100) / 100);

  return (
    <RequestCard
      tone={open && !askPartlySettled ? "payment" : "settled"}
      badge={
        open
          ? payment.method === "INSTAPAY"
            ? "send"
            : "register"
          : payment.state === "VERIFIED"
            ? "check"
            : "back"
      }
      testId="diner-payment"
      title={
        <span data-testid="diner-payment-title">
          {t((m) =>
            payment.method === "INSTAPAY"
              ? m.pos.dinerPaymentTitle
              : m.pos.dinerPaymentTitleAsk,
          )}
        </span>
      }
      subtitle={
        <>
          {methodLabel}
          {open ? null : (
            <>
              {" · "}
              {t((m) =>
                payment.state === "VERIFIED"
                  ? m.pos.dinerPaymentVerified
                  : m.pos.dinerPaymentRejected,
              )}
            </>
          )}
        </>
      }
      aside={open ? null : money(payment.amount)}
      footer={
        <>
          {open && !askPartlySettled ? (
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[13px] text-[color:var(--text-secondary)]">
                {t((m) => m.pos.dinerPaymentAmount)}
              </span>
              <span className="whitespace-nowrap text-[22px] font-bold tabular-nums">
                {money(payment.amount)}
              </span>
            </div>
          ) : null}
          {split !== null ? (
            <p className="text-xs font-bold" data-testid="diner-payment-share">
              {split.mode === "EVEN"
                ? t(
                    countedKey(split.people, {
                      one: (m) => m.pos.dinerPaymentShareEvenOne,
                      two: (m) => m.pos.dinerPaymentShareEvenTwo,
                      few: (m) => m.pos.dinerPaymentShareEvenFew,
                      many: (m) => m.pos.dinerPaymentShareEven,
                    }),
                    { count: split.people },
                  )
                : t(
                    countedKey(split.itemIds.length, {
                      one: (m) => m.pos.dinerPaymentShareItemsOne,
                      two: (m) => m.pos.dinerPaymentShareItemsTwo,
                      few: (m) => m.pos.dinerPaymentShareItemsFew,
                      many: (m) => m.pos.dinerPaymentShareItems,
                    }),
                    { count: split.itemIds.length },
                  )}
            </p>
          ) : null}
          {tipAmount > 0 ? (
            <div className="flex flex-col gap-1 border-t border-[color:var(--border-light)] pt-2">
              <p className="text-xs text-[color:var(--text-secondary)]" data-testid="diner-payment-tip">
                {/* A tip of an AMOUNT (the table app's 20 / 30 / 50 / Other) has
                    no percent — `tipPercent` is null — so it reads "+ tip,
                    30.00 EGP". */}
                {typeof payment.tipPercent === "number" && payment.tipPercent > 0
                  ? t((m) => m.pos.dinerPaymentTip, {
                      percent: payment.tipPercent,
                      amount: money(tipAmount),
                    })
                  : t((m) => m.pos.dinerPaymentTipAmount, {
                      amount: money(tipAmount),
                    })}
              </p>
              <p className="text-sm font-semibold" data-testid="diner-payment-collect">
                {t((m) => m.pos.dinerPaymentCollect, { amount: collectAmount })}
              </p>
            </div>
          ) : null}

          {payment.proofUrl ? (
            <a
              href={payment.proofUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-[var(--radius-md,6px)] bg-[color:var(--surface-muted,var(--surface))] px-3 py-2 text-[13px] font-semibold underline-offset-2 hover:underline"
            >
              {t((m) => m.pos.dinerPaymentViewProof)}
            </a>
          ) : open && payment.method === "INSTAPAY" ? (
            <p className="text-xs text-[color:var(--muted-foreground)]">
              {t((m) => m.pos.dinerPaymentNoProof)}
            </p>
          ) : null}

          {gap !== 0 ? (
            <p
              className="rounded-[var(--radius-chip)] px-2 py-1 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold"
              style={{
                background: gap > 0 ? "var(--warning-100)" : "var(--danger-100)",
                color: gap > 0 ? "var(--warning)" : "var(--danger)",
              }}
              data-testid="diner-payment-gap"
            >
              {t(
                (m) =>
                  gap > 0 ? m.pos.dinerPaymentShort : m.pos.dinerPaymentOver,
                {
                  // The BILL as well as the gap: the figure to ask for is the
                  // one the sentence is about.
                  total: money(invoiceTotal ?? 0),
                  gap: money(Math.abs(gap)),
                },
              )}
            </p>
          ) : null}

          {shareSettled ? (
            <p
              className="flex items-center gap-1.5 text-[13px] font-semibold text-[color:var(--success)]"
              data-testid="diner-payment-share-settled"
            >
              {askPartlySettled && typeof invoiceTotal === "number" && invoiceTotal > 0
                ? t((m) => m.pos.dinerPaymentAskPartSettled, {
                    remaining: money(invoiceTotal),
                  })
                : t((m) => m.pos.dinerPaymentShareSettled)}
            </p>
          ) : null}

          {collectFirst || settleAfter ? (
            <div className="flex items-center gap-2">
              <Button
                className="flex-1"
                variant="primary"
                size="touch"
                disabled={isSubmitting}
                onClick={onSettleShare}
                data-testid="diner-payment-settle-share"
              >
                {split !== null
                  ? t((m) => m.pos.dinerPaymentSettleShare)
                  : t((m) => m.pos.dinerPaymentCollectNow, {
                      amount: money(payment.amount),
                    })}
              </Button>
              {open ? (
                <button
                  type="button"
                  className="px-1 text-[13px] text-[color:var(--text-secondary)] underline underline-offset-2 disabled:opacity-50"
                  disabled={isSubmitting}
                  onClick={() => onReview(false)}
                  data-testid="diner-payment-reject"
                >
                  {t((m) => m.pos.dinerPaymentWontPay)}
                </button>
              ) : null}
            </div>
          ) : open && !askPartlySettled ? (
            <div className="flex items-center gap-2">
              <Button
                className="flex-1"
                variant="primary"
                size="touch"
                disabled={isSubmitting}
                onClick={() => onReview(true)}
                data-testid="diner-payment-confirm"
              >
                {t((m) => m.pos.dinerPaymentConfirm)}
              </Button>
              <button
                type="button"
                className="px-1 text-[13px] text-[color:var(--text-secondary)] underline underline-offset-2 disabled:opacity-50"
                disabled={isSubmitting}
                onClick={() => onReview(false)}
                data-testid="diner-payment-reject"
              >
                {t((m) => m.pos.dinerPaymentReject)}
              </button>
            </div>
          ) : null}
          {open && !collectFirst && !askPartlySettled ? (
            <p className="text-[length:var(--font-size-2xs)] text-[color:var(--muted-foreground)]">
              {t((m) => m.pos.dinerPaymentNote)}
            </p>
          ) : null}

          {/* A cash tip into the drawer, one tap, keyed on the claim. A link,
              not a second button: the card's one answer is above it. */}
          {payment.state !== "REJECTED" &&
          onRecordCashTip &&
          payment.method === "CASH" &&
          tipAmount > 0 ? (
            tipRecorded ? (
              <p className="text-xs font-bold text-[color:var(--success)]">
                {t((m) => m.pos.dinerPaymentTipInDrawer)}
              </p>
            ) : (
              <button
                type="button"
                className="self-start text-[13px] text-[color:var(--text-secondary)] underline underline-offset-2 disabled:opacity-50"
                disabled={isSubmitting || tipPending}
                onClick={async () => {
                  setTipPending(true);
                  const ok = await onRecordCashTip();
                  setTipPending(false);
                  if (ok) setTipRecorded(true);
                }}
                data-testid="diner-payment-tip-drawer"
              >
                {t((m) => m.pos.dinerPaymentTipToDrawer, {
                  amount: money(tipAmount),
                })}
              </button>
            )
          ) : null}
        </>
      }
    />
  );
}
